import { Dropdown } from 'primereact/dropdown';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

type MultiFilterChipsInputProps = {
  title: string;
  placeholder?: string;
  filterOptions: { title: string; bgClassName: string }[];
};

const MultiFilterChipsInput = ({
  title,
  placeholder,
  filterOptions,
}: MultiFilterChipsInputProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { handleSubmit, control, setValue } = useForm({
    defaultValues: {
      inputText: '',
      selectedFilter: filterOptions[0] || '',
    },
  });

  const [termsList, setTermsList] = useState<
    { term: string; type: string; bgClassName: string }[]
  >([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onSubmit = (e: any) => {
    let addToQueryString = false;
    setTermsList((prev) => {
      // If the provided term matches exactly as other one of the same type dont do anything
      if (
        prev.find(
          (singleTerm) =>
            singleTerm.term.toLowerCase() === e.inputText.toLowerCase() &&
            singleTerm.type === e.selectedFilter.title
        )
      ) {
        return prev;
      }

      addToQueryString = true;
      // Adds the new term to the querystring and the chips
      const newTermsList = [
        ...prev,
        {
          term: e.inputText,
          type: e.selectedFilter.title,
          bgClassName: e.selectedFilter.bgClassName,
        },
      ];

      setValue('inputText', '');
      return newTermsList;
    });

    if (addToQueryString) {
      const queryString = new URLSearchParams(searchParams);
      queryString.append(e.selectedFilter.title, e.inputText);
      setSearchParams(queryString);
    }
  };

  const dropdownTemplate = ({ title: DropDownTitle }: { title: string }) => (
    <span>Search by {DropDownTitle}</span>
  );

  const dropdownOnChange = () => {
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 50);
  };

  const deleteSingleTermList = (term: any) => {
    let deleteTerm = false;
    setTermsList((prev) => {
      const cleanedTermList = prev.filter((singleTerm) => {
        if (singleTerm.type === term.type && singleTerm.term === term.term) {
          deleteTerm = true;
          return false;
        }
        return true;
      });

      return cleanedTermList;
    });

    if (deleteTerm) {
      const queryString = new URLSearchParams(searchParams);
      // @ts-ignore
      queryString.delete(term.type, term.term);
      setSearchParams(queryString);
    }
  };

  useEffect(() => {
    setTermsList(
      Array.from(searchParams.entries()).reduce(
        (prev, act) => {
          const findedFitlerOption = filterOptions.find(
            (singleFilterOption) => singleFilterOption.title === act[0]
          );
          if (findedFitlerOption) {
            prev.push({
              term: act[1],
              type: act[0],
              bgClassName: findedFitlerOption.bgClassName,
            });
          }

          return prev;
        },
        [] as { term: string; type: string; bgClassName: string }[]
      )
    );

    return () => setTermsList([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-full">
      <h3 className="font-semibold mb-2">{title}</h3>
      <div className="w-full">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-row">
          <Controller
            control={control}
            name="selectedFilter"
            render={({ field }) => (
              <Dropdown
                className="rounded-none rounded-l border-y-0 border-l-0"
                options={filterOptions}
                optionLabel="title"
                inputId="multiFilterChipsDropdown"
                valueTemplate={dropdownTemplate}
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  dropdownOnChange();
                }}
              />
            )}
          />
          <Controller
            name="inputText"
            control={control}
            render={({ field }) => (
              <input
                ref={(e) => {
                  inputRef.current = e;
                  field.ref(e);
                }}
                type="text"
                className="border-r-0 border-solid rounded-r px-4 py-2 w-full outline-none"
                placeholder={placeholder}
                value={field.value || ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit(onSubmit)();
                  }
                }}
              />
            )}
          />
        </form>
        <div className="flex flex-row gap-4 mt-4">
          {termsList &&
            termsList.length > 0 &&
            termsList.map((singleTermList) => {
              return (
                <div
                  key={`${singleTermList.term}_${singleTermList.type}`}
                  className={`flex flex-row items-end rounded-full px-4 py-2 text-white ${singleTermList.bgClassName}`}
                >
                  <div className="flex flex-col">
                    <span className="text-xs -mb-1">{singleTermList.type}</span>
                    <span>{singleTermList.term}</span>
                  </div>
                  <button
                    type="button"
                    aria-label="Delete"
                    onClick={() => deleteSingleTermList(singleTermList)}
                  >
                    <i className="pi pi-times text-sm font-extrabold ml-4" />
                  </button>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};
MultiFilterChipsInput.defaultProps = {
  placeholder: '',
};

export default MultiFilterChipsInput;
